import { h, Component, Host, State, Element, Prop, Watch } from "@stencil/core";
import { ColorableComponent, applySchemeDelegate, setCssVariablesDelegate } from "../../common/colorable-component";
import { PreloaderSquaresSchemeDefinition, PreloaderSquaresSchemeName } from "./constants/preloader-squares-types";
import { PRELOADER_SQUARES_SCHEME_CONFIG, PRELOADER_SQUARES_SCHEME_MAP } from "./constants/preloader-squares-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { PreloaderSquaresSchemeConfiguration } from "../../constants/colors";
import { IS_SQUIRCLE } from "../../constants/global-constants";

@Component({
  tag: "eon-ui-preloader-squares",
  styleUrl: "preloader-squares.scss",
  shadow: true
})
export class PreloaderSquares
  implements ColorableComponent<PreloaderSquaresSchemeConfiguration<PreloaderSquaresSchemeDefinition>> {
  schemeConfig: PreloaderSquaresSchemeConfiguration<PreloaderSquaresSchemeDefinition> = PRELOADER_SQUARES_SCHEME_CONFIG;
  schemeMap = PRELOADER_SQUARES_SCHEME_MAP;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: PreloaderSquaresSchemeDefinition;

  @Element() hostElement: HTMLEonUiPreloaderSquaresElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: PreloaderSquaresSchemeName;

  /**
   * Text under the preloader
   */
  @Prop() loaderText: string;

  componentWillLoad() {
    this.applyScheme();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  render() {
    return (
      <Host>
        {IS_SQUIRCLE ? (
          <div class={pfx("preloader-wrapper")}>
            <div class={pfx("preloader-svg")}>
              <svg xmlns="http://www.w3.org/2000/svg" width="88px" height="16px" viewBox="0 0 88 16">
                <path
                  class={pfx({ "preloader-shape": true, "preloader-shape-1": true })}
                  d="M0.514736378,2.58933334 C-0.171578793,6.19666659 -0.171578793,9.80366629 0.514736378,13.4109995 C0.714659503,14.4629631 1.53741305,15.2857382 2.58934913,15.4856665 C4.37269108,15.8267383 6.18421221,15.9989445 7.999875,16 C9.80316139,16 11.6074478,15.8283336 13.4107342,15.4853336 C14.4626779,15.2855413 15.2853955,14.46267 15.4850136,13.4106667 C16.1716621,9.80333341 16.1716621,6.19633371 15.4850136,2.58900045 C15.2855681,1.53690603 14.4627692,0.713953417 13.4107342,0.514333514 C11.6272812,0.173252611 9.81564855,0.00104643635 7.999875,0 C6.18420171,0.00116523817 4.37268048,0.173482889 2.58934913,0.5146664 C1.53732191,0.714422269 0.714487162,1.5372785 0.514736378,2.58933334 Z"
                ></path>
                <path
                  class={pfx({ "preloader-shape": true, "preloader-shape-2": true })}
                  d="M0.514736378,2.58933334 C-0.171578793,6.19666659 -0.171578793,9.80366629 0.514736378,13.4109995 C0.714659503,14.4629631 1.53741305,15.2857382 2.58934913,15.4856665 C4.37269108,15.8267383 6.18421221,15.9989445 7.999875,16 C9.80316139,16 11.6074478,15.8283336 13.4107342,15.4853336 C14.4626779,15.2855413 15.2853955,14.46267 15.4850136,13.4106667 C16.1716621,9.80333341 16.1716621,6.19633371 15.4850136,2.58900045 C15.2855681,1.53690603 14.4627692,0.713953417 13.4107342,0.514333514 C11.6272812,0.173252611 9.81564855,0.00104643635 7.999875,0 C6.18420171,0.00116523817 4.37268048,0.173482889 2.58934913,0.5146664 C1.53732191,0.714422269 0.714487162,1.5372785 0.514736378,2.58933334 Z"
                  transform="translate(24 0)"
                ></path>
                <path
                  class={pfx({ "preloader-shape": true, "preloader-shape-3": true })}
                  d="M0.514736378,2.58933334 C-0.171578793,6.19666659 -0.171578793,9.80366629 0.514736378,13.4109995 C0.714659503,14.4629631 1.53741305,15.2857382 2.58934913,15.4856665 C4.37269108,15.8267383 6.18421221,15.9989445 7.999875,16 C9.80316139,16 11.6074478,15.8283336 13.4107342,15.4853336 C14.4626779,15.2855413 15.2853955,14.46267 15.4850136,13.4106667 C16.1716621,9.80333341 16.1716621,6.19633371 15.4850136,2.58900045 C15.2855681,1.53690603 14.4627692,0.713953417 13.4107342,0.514333514 C11.6272812,0.173252611 9.81564855,0.00104643635 7.999875,0 C6.18420171,0.00116523817 4.37268048,0.173482889 2.58934913,0.5146664 C1.53732191,0.714422269 0.714487162,1.5372785 0.514736378,2.58933334 Z"
                  transform="translate(48 0)"
                ></path>
                <path
                  class={pfx({ "preloader-shape": true, "preloader-shape-4": true })}
                  d="M0.514736378,2.58933334 C-0.171578793,6.19666659 -0.171578793,9.80366629 0.514736378,13.4109995 C0.714659503,14.4629631 1.53741305,15.2857382 2.58934913,15.4856665 C4.37269108,15.8267383 6.18421221,15.9989445 7.999875,16 C9.80316139,16 11.6074478,15.8283336 13.4107342,15.4853336 C14.4626779,15.2855413 15.2853955,14.46267 15.4850136,13.4106667 C16.1716621,9.80333341 16.1716621,6.19633371 15.4850136,2.58900045 C15.2855681,1.53690603 14.4627692,0.713953417 13.4107342,0.514333514 C11.6272812,0.173252611 9.81564855,0.00104643635 7.999875,0 C6.18420171,0.00116523817 4.37268048,0.173482889 2.58934913,0.5146664 C1.53732191,0.714422269 0.714487162,1.5372785 0.514736378,2.58933334 Z"
                  transform="translate(72 0)"
                ></path>
              </svg>
            </div>
            {this.loaderText && <div class={pfx("preloader-text")}>{this.loaderText}</div>}
          </div>
        ) : (
          <div class={pfx("preloader-wrapper")}>
            <div class={pfx("preloader-squares")}>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
            {this.loaderText && <div class={pfx("preloader-text")}>{this.loaderText}</div>}
          </div>
        )}
      </Host>
    );
  }
}
